import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, Polyline } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const COLORS = {
  lowRisk: "#10b981",
  moderateRisk: "#f59e0b",
  highRisk: "#ef4444",
  route: "#06b6d4",
  onDark: "rgba(255,255,255,0.90)",
};

const MAP_CENTER: [number, number] = [7, 18];
const CAR_CENTER = { lat: 6.6111, lng: 20.9394 };
const CAR_COORDINATES: [number, number] = [CAR_CENTER.lat, CAR_CENTER.lng];

type Centroid = { lat: number; lng: number; name: string };

/** Canonical English key → coords */
const COUNTRY_CENTROIDS: Record<string, Centroid> = {
  guinea: { lat: 9.9456, lng: -9.6966, name: "Guinea" },
  "sierra leone": { lat: 8.4606, lng: -11.7799, name: "Sierra Leone" },
  liberia: { lat: 6.4281, lng: -9.4295, name: "Liberia" },
  "democratic republic of the congo": { lat: -4.0383, lng: 21.7587, name: "DRC" },
  uganda: { lat: 1.3733, lng: 32.2903, name: "Uganda" },
  kenya: { lat: -0.0236, lng: 37.9062, name: "Kenya" },
  nigeria: { lat: 9.082, lng: 8.6753, name: "Nigeria" },
  cameroon: { lat: 3.848, lng: 11.5021, name: "Cameroon" },
  "south sudan": { lat: 6.877, lng: 31.307, name: "South Sudan" },
  ethiopia: { lat: 9.145, lng: 40.4897, name: "Ethiopia" },
  malawi: { lat: -13.2543, lng: 34.3015, name: "Malawi" },
  mozambique: { lat: -18.6657, lng: 35.5296, name: "Mozambique" },
  tanzania: { lat: -6.369, lng: 34.8888, name: "Tanzania" },
  zambia: { lat: -13.1339, lng: 27.8493, name: "Zambia" },
  zimbabwe: { lat: -19.0154, lng: 29.1549, name: "Zimbabwe" },
  "south africa": { lat: -30.5595, lng: 22.9375, name: "South Africa" },
  angola: { lat: -11.2027, lng: 17.8739, name: "Angola" },
  "republic of the congo": { lat: -0.228, lng: 15.8277, name: "Congo" },
  gabon: { lat: -0.8037, lng: 11.6045, name: "Gabon" },
  "central african republic": { lat: 6.6111, lng: 20.9394, name: "CAR" },
  chad: { lat: 15.4542, lng: 18.7322, name: "Chad" },
  sudan: { lat: 12.8628, lng: 30.2176, name: "Sudan" },
  egypt: { lat: 26.8206, lng: 30.8025, name: "Egypt" },
  ghana: { lat: 7.9465, lng: -1.0232, name: "Ghana" },
  "ivory coast": { lat: 7.54, lng: -5.5471, name: "Côte d'Ivoire" },
  benin: { lat: 9.3077, lng: 2.3158, name: "Benin" },
  togo: { lat: 8.6195, lng: 0.8248, name: "Togo" },
  mali: { lat: 17.5707, lng: -3.9962, name: "Mali" },
  "burkina faso": { lat: 12.2383, lng: -1.5616, name: "Burkina Faso" },
  niger: { lat: 17.6078, lng: 8.0817, name: "Niger" },
  senegal: { lat: 14.4974, lng: -14.4524, name: "Senegal" },
  rwanda: { lat: -1.9403, lng: 29.8739, name: "Rwanda" },
  burundi: { lat: -3.3731, lng: 29.9189, name: "Burundi" },
  "equatorial guinea": { lat: 1.6508, lng: 10.2679, name: "Equatorial Guinea" },
  france: { lat: 46.2276, lng: 2.2137, name: "France" },
  "united states": { lat: 37.0902, lng: -95.7129, name: "United States" },
  "united kingdom": { lat: 55.3781, lng: -3.436, name: "United Kingdom" },
  canada: { lat: 56.1304, lng: -106.3468, name: "Canada" },
  belgium: { lat: 50.5039, lng: 4.4699, name: "Belgium" },
  netherlands: { lat: 52.1326, lng: 5.2913, name: "Netherlands" },
  germany: { lat: 51.1657, lng: 10.4515, name: "Germany" },
  italy: { lat: 41.8719, lng: 12.5674, name: "Italy" },
  spain: { lat: 40.4637, lng: -3.7492, name: "Spain" },
  switzerland: { lat: 46.8182, lng: 8.2275, name: "Switzerland" },
  australia: { lat: -25.2744, lng: 133.7751, name: "Australia" },
  india: { lat: 20.5937, lng: 78.9629, name: "India" },
  china: { lat: 35.8617, lng: 104.1954, name: "China" },
  japan: { lat: 36.2048, lng: 138.2529, name: "Japan" },
  thailand: { lat: 15.87, lng: 100.9925, name: "Thailand" },
  singapore: { lat: 1.3521, lng: 103.8198, name: "Singapore" },
  morocco: { lat: 31.7917, lng: -7.0926, name: "Morocco" },
  algeria: { lat: 28.0339, lng: 1.6596, name: "Algeria" },
  tunisia: { lat: 33.8869, lng: 9.5375, name: "Tunisia" },
  turkey: { lat: 38.9637, lng: 35.2433, name: "Turkey" },
  brazil: { lat: -14.235, lng: -51.9253, name: "Brazil" },
  portugal: { lat: 39.3999, lng: -8.2245, name: "Portugal" },
  russia: { lat: 61.524, lng: 105.3188, name: "Russia" },
  "united arab emirates": { lat: 23.4241, lng: 53.8478, name: "UAE" },
  "saudi arabia": { lat: 23.8859, lng: 45.0792, name: "Saudi Arabia" },
};

/**
 * Alias / French / ISO / local spellings → canonical centroid key.
 * Exact alias match is preferred so "Nigeria" does not resolve to "Niger".
 */
const COUNTRY_ALIASES: Record<string, string> = {
  // CAR neighbours / Francophone Africa
  cameroun: "cameroon",
  cameroon: "cameroon",
  cm: "cameroon",
  tchad: "chad",
  chad: "chad",
  td: "chad",
  "soudan du sud": "south sudan",
  "south sudan": "south sudan",
  ss: "south sudan",
  soudan: "sudan",
  sudan: "sudan",
  sd: "sudan",
  "republique democratique du congo": "democratic republic of the congo",
  "république démocratique du congo": "democratic republic of the congo",
  "democratic republic of the congo": "democratic republic of the congo",
  "democratic republic of congo": "democratic republic of the congo",
  "dr congo": "democratic republic of the congo",
  "d.r. congo": "democratic republic of the congo",
  "drc": "democratic republic of the congo",
  rdc: "democratic republic of the congo",
  cd: "democratic republic of the congo",
  "republique du congo": "republic of the congo",
  "république du congo": "republic of the congo",
  "republic of the congo": "republic of the congo",
  "congo-brazzaville": "republic of the congo",
  "congo brazzaville": "republic of the congo",
  congo: "republic of the congo",
  cg: "republic of the congo",
  "republique centrafricaine": "central african republic",
  "république centrafricaine": "central african republic",
  "central african republic": "central african republic",
  "centrafrique": "central african republic",
  rca: "central african republic",
  cf: "central african republic",
  car: "central african republic",
  gabon: "gabon",
  ga: "gabon",
  "guinee equatoriale": "equatorial guinea",
  "guinée équatoriale": "equatorial guinea",
  "equatorial guinea": "equatorial guinea",
  gq: "equatorial guinea",
  angola: "angola",
  ao: "angola",
  nigeria: "nigeria",
  nigéria: "nigeria",
  ng: "nigeria",
  niger: "niger",
  ne: "niger",
  "cote d'ivoire": "ivory coast",
  "côte d'ivoire": "ivory coast",
  "cote divoire": "ivory coast",
  "ivory coast": "ivory coast",
  ci: "ivory coast",
  senegal: "senegal",
  sénégal: "senegal",
  sn: "senegal",
  mali: "mali",
  ml: "mali",
  "burkina faso": "burkina faso",
  burkina: "burkina faso",
  bf: "burkina faso",
  togo: "togo",
  tg: "togo",
  benin: "benin",
  bénin: "benin",
  bj: "benin",
  ghana: "ghana",
  gh: "ghana",
  guinee: "guinea",
  guinée: "guinea",
  guinea: "guinea",
  gn: "guinea",
  liberia: "liberia",
  libéria: "liberia",
  lr: "liberia",
  "sierra leone": "sierra leone",
  sl: "sierra leone",
  rwanda: "rwanda",
  rw: "rwanda",
  burundi: "burundi",
  bi: "burundi",
  ouganda: "uganda",
  uganda: "uganda",
  ug: "uganda",
  kenya: "kenya",
  ke: "kenya",
  ethiopie: "ethiopia",
  éthiopie: "ethiopia",
  ethiopia: "ethiopia",
  et: "ethiopia",
  egypte: "egypt",
  égypte: "egypt",
  egypt: "egypt",
  eg: "egypt",
  "afrique du sud": "south africa",
  "south africa": "south africa",
  za: "south africa",
  tanzania: "tanzania",
  tanzanie: "tanzania",
  tz: "tanzania",
  zambie: "zambia",
  zambia: "zambia",
  zm: "zambia",
  zimbabwe: "zimbabwe",
  zw: "zimbabwe",
  mozambique: "mozambique",
  mz: "mozambique",
  malawi: "malawi",
  mw: "malawi",
  // Europe / other
  france: "france",
  fr: "france",
  belgique: "belgium",
  belgium: "belgium",
  be: "belgium",
  "etats-unis": "united states",
  "états-unis": "united states",
  "united states": "united states",
  usa: "united states",
  us: "united states",
  "royaume-uni": "united kingdom",
  "united kingdom": "united kingdom",
  uk: "united kingdom",
  gb: "united kingdom",
  allemagne: "germany",
  germany: "germany",
  de: "germany",
  italie: "italy",
  italy: "italy",
  it: "italy",
  espagne: "spain",
  spain: "spain",
  es: "spain",
  suisse: "switzerland",
  switzerland: "switzerland",
  ch: "switzerland",
  "pays-bas": "netherlands",
  netherlands: "netherlands",
  nl: "netherlands",
  canada: "canada",
  ca: "canada",
  inde: "india",
  india: "india",
  in: "india",
  chine: "china",
  china: "china",
  cn: "china",
  maroc: "morocco",
  morocco: "morocco",
  ma: "morocco",
  algerie: "algeria",
  algérie: "algeria",
  algeria: "algeria",
  dz: "algeria",
  tunisie: "tunisia",
  tunisia: "tunisia",
  tn: "tunisia",
  turquie: "turkey",
  turkey: "turkey",
  tr: "turkey",
  "emirats arabes unis": "united arab emirates",
  "émirats arabes unis": "united arab emirates",
  uae: "united arab emirates",
  ae: "united arab emirates",
  "arabie saoudite": "saudi arabia",
  "saudi arabia": "saudi arabia",
  sa: "saudi arabia",
  bresil: "brazil",
  brésil: "brazil",
  brazil: "brazil",
  br: "brazil",
  portugal: "portugal",
  pt: "portugal",
  russie: "russia",
  russia: "russia",
  ru: "russia",
  japon: "japan",
  japan: "japan",
  jp: "japan",
  thailande: "thailand",
  thaïlande: "thailand",
  thailand: "thailand",
  th: "thailand",
  singapour: "singapore",
  singapore: "singapore",
  sg: "singapore",
  australie: "australia",
  australia: "australia",
  au: "australia",
};

const stripDiacritics = (value: string) =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const normalizeCountryLabel = (country: string) =>
  stripDiacritics((country || "").toLowerCase().trim())
    .replace(/['’`]/g, "'")
    .replace(/[^a-z0-9\s\-']/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveCentroid = (country: string): (Centroid & { key: string }) | null => {
  const normalized = normalizeCountryLabel(country);
  if (!normalized || normalized === "unknown" || normalized === "n a" || normalized === "na") {
    return null;
  }

  // 1) Exact alias / canonical key
  const aliasKey = COUNTRY_ALIASES[normalized] || (COUNTRY_CENTROIDS[normalized] ? normalized : null);
  if (aliasKey && COUNTRY_CENTROIDS[aliasKey]) {
    return { key: aliasKey, ...COUNTRY_CENTROIDS[aliasKey] };
  }

  // 2) Starts-with / contained alias (longest alias wins — avoids niger ⊂ nigeria)
  let best: { alias: string; key: string } | null = null;
  for (const [alias, key] of Object.entries(COUNTRY_ALIASES)) {
    if (normalized === alias) {
      best = { alias, key };
      break;
    }
    if (normalized.startsWith(alias + " ") || alias.startsWith(normalized + " ")) {
      if (!best || alias.length > best.alias.length) best = { alias, key };
    }
  }
  if (best && COUNTRY_CENTROIDS[best.key]) {
    return { key: best.key, ...COUNTRY_CENTROIDS[best.key] };
  }

  // 3) Fuzzy contains on canonical keys only (length ≥ 5 to avoid short false hits)
  for (const [key, centroid] of Object.entries(COUNTRY_CENTROIDS)) {
    if (key.length < 5) continue;
    if (normalized.includes(key) || key.includes(normalized)) {
      return { key, ...centroid };
    }
  }

  return null;
};

// A country renders red once its share of the DB-wide total high-risk count
// exceeds this threshold — not merely for having any high-risk case at all.
const HIGH_RISK_SHARE_THRESHOLD_PCT = 9;

const getRiskColorByShare = (highRisk: number, totalHighRisk: number) => {
  const pct = totalHighRisk > 0 ? (highRisk / totalHighRisk) * 100 : 0;
  return pct > HIGH_RISK_SHARE_THRESHOLD_PCT ? COLORS.highRisk : COLORS.lowRisk;
};

const getRiskLevelByShare = (highRisk: number, totalHighRisk: number) => {
  const pct = totalHighRisk > 0 ? (highRisk / totalHighRisk) * 100 : 0;
  return pct > HIGH_RISK_SHARE_THRESHOLD_PCT ? "High Risk" : "Low Risk";
};

const getMarkerRadius = (count?: number) => Math.max(6, Math.min(22, 5 + Math.log10((count || 1) + 1) * 6));

type Hotspot = {
  country: string;
  countryName: string;
  coords: { lat: number; lng: number };
  count: number;
  highRisk: number;
  riskLevel: string;
  color: string;
  radius: number;
};

type MapPoint = {
  country?: string;
  highRisk?: number;
  count?: number;
};

type Props = {
  points?: MapPoint[];
  /** DB-wide total high-risk count (data.summary.high_risk) — denominator for the red/green share threshold. */
  totalHighRisk?: number;
  /** @deprecated unused — kept so older DashboardPage builds still compile */
  selectedCountry?: string;
};

const GeoDashboardMap: React.FC<Props> = ({ points = [], totalHighRisk = 0 }) => {
  const [hovered, setHovered] = useState<Hotspot | null>(null);

  const hotspots = useMemo<Hotspot[]>(() => {
    const byKey = new Map<string, Hotspot>();

    // Pass 1 — aggregate raw counts per country (color depends on the final
    // merged high-risk sum vs. the DB-wide total, so it can't be assigned per-point).
    for (const point of points) {
      const country = point.country;
      if (!country) continue;

      const resolved = resolveCentroid(country);
      if (!resolved) continue;

      const count = point.count && point.count > 0 ? point.count : 1;
      const highRisk = point.highRisk || 0;
      const existing = byKey.get(resolved.key);

      if (existing) {
        existing.count += count;
        existing.highRisk += highRisk;
        existing.radius = getMarkerRadius(existing.count);
      } else {
        byKey.set(resolved.key, {
          country: resolved.key,
          countryName: resolved.name,
          coords: { lat: resolved.lat, lng: resolved.lng },
          count,
          highRisk,
          riskLevel: "Low Risk",
          color: COLORS.lowRisk,
          radius: getMarkerRadius(count),
        });
      }
    }

    // Pass 2 — assign color/label from each country's share of the DB-wide total.
    const list = Array.from(byKey.values());
    for (const h of list) {
      h.riskLevel = getRiskLevelByShare(h.highRisk, totalHighRisk);
      h.color = getRiskColorByShare(h.highRisk, totalHighRisk);
    }

    return list.sort((a, b) => b.count - a.count);
  }, [points, totalHighRisk]);

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", borderRadius: "16px", overflow: "hidden" }}>
      <MapContainer center={MAP_CENTER} zoom={3} scrollWheelZoom={false} style={{ width: "100%", height: "100%" }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {hotspots.map((hotspot) => (
          <CircleMarker
            key={`marker-${hotspot.country}`}
            center={[hotspot.coords.lat, hotspot.coords.lng]}
            radius={hotspot.radius}
            pathOptions={{
              color: hotspot.color,
              fillColor: hotspot.color,
              fillOpacity: 0.75,
              weight: 1.5,
            }}
            eventHandlers={{
              mouseover: () => setHovered(hotspot),
              mouseout: () => setHovered(null),
              click: () => setHovered(hotspot),
            }}
          />
        ))}

        {hotspots.map((hotspot) => (
          <Polyline
            key={`route-${hotspot.country}`}
            positions={[[hotspot.coords.lat, hotspot.coords.lng], CAR_COORDINATES]}
            pathOptions={{
              color: COLORS.route,
              weight: 2,
              opacity: 0.55,
              dashArray: "8",
            }}
          />
        ))}
      </MapContainer>

      {/* React hover card — avoids blank Leaflet tooltip/popup */}
      {hovered && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            background: "#0f172a",
            color: "#f8fafc",
            border: "1px solid rgba(148,163,184,0.35)",
            borderRadius: "10px",
            px: 2,
            py: 1.25,
            minWidth: 180,
            boxShadow: "0 10px 28px rgba(15,23,42,0.35)",
            pointerEvents: "none",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", mb: 0.5 }}>
            {hovered.countryName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>
            Travelers: <strong style={{ color: "#fff" }}>{hovered.count.toLocaleString()}</strong>
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>
            Risk: <strong style={{ color: hovered.color }}>{hovered.riskLevel}</strong>
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          left: 20,
          background: "rgba(15, 23, 42, 0.85)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(148, 163, 184, 0.2)",
          borderRadius: "12px",
          p: 1.5,
          minWidth: 180,
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          zIndex: 500,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: COLORS.onDark,
            mb: 1,
            opacity: 0.7,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Legend · {hotspots.length} origins
        </Typography>
        {[
          { color: COLORS.lowRisk, label: "Low Risk" },
          { color: COLORS.highRisk, label: "High Risk" },
          { color: COLORS.route, label: "Travel Routes" },
        ].map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
            {item.label === "Travel Routes" ? (
              <Box sx={{ width: 16, height: 2, background: item.color, borderRadius: "1px" }} />
            ) : (
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
            )}
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default GeoDashboardMap;
